import { IconPlayerStop, IconSend2 } from '@tabler/icons-solidjs'
import { createEffect, type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv } from '../lib/tv.ts'
import type { FileAttachmentLayout } from './file-attachment-list.tsx'
import { FileDropzone } from './file-dropzone.tsx'

const aiComposer = tv({
  slots: {
    form: 'grid gap-2',
    frame: [
      'rounded-[0.875rem] bg-surface shadow-[0_0_0_1px_var(--color-border)]',
      'transition-[border-color,box-shadow] duration-(--duration-fast) ease-out',
      'focus-within:shadow-[0_0_0_1px_var(--color-accent),0_0_0_4px_var(--color-accent-soft)]',
    ],
    field: 'flex items-end gap-1.5 p-3',
    textarea: [
      'min-h-9 max-md:min-h-(--size-touch) max-h-36 min-w-0 flex-1 resize-none overflow-y-hidden bg-transparent px-1 py-2',
      'text-body text-text outline-none placeholder:text-text-subtle',
      'disabled:cursor-not-allowed disabled:text-text-subtle',
    ],
    action: [
      'flex size-9 shrink-0 items-center justify-center rounded-full max-md:size-(--size-touch)',
      'text-text-muted transition-[background-color,color,transform]',
      'duration-(--duration-press) ease-out active:scale-[0.98] motion-reduce:active:scale-100',
      'hover:bg-surface-sunken hover:text-text',
      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
      'disabled:pointer-events-none disabled:opacity-45',
    ],
    submit: 'bg-accent text-text-inverse hover:bg-accent-hover hover:text-text-inverse',
    dropzone: 'border-b border-border p-2',
    footnote: 'px-1 text-micro text-text-subtle',
  },
})

export interface AiComposerProps {
  value: string
  onValueChange: (value: string) => void
  onSubmit: (value: string, files: File[]) => void
  files?: File[]
  onFilesChange?: (files: File[]) => void
  accept?: string
  maxSizeBytes?: number
  onFileReject?: (reason: string, file: File) => void
  fileLabel?: string
  fileHint?: string
  fileLayout?: FileAttachmentLayout
  /** True while the assistant is producing a reply — blocks submit, shows the busy affordance. */
  busy?: boolean
  onStop?: () => void
  disabled?: boolean
  placeholder?: string
  footer?: JSX.Element
  attachments?: JSX.Element
  minRows?: number
  /** Rendered under the input; used for the "assistant proposes, a person commits" line. */
  footnote?: string
}

export const AiComposer = (props: AiComposerProps) => {
  const [local] = splitProps(props, [
    'value',
    'onValueChange',
    'onSubmit',
    'files',
    'onFilesChange',
    'accept',
    'maxSizeBytes',
    'onFileReject',
    'fileLabel',
    'fileHint',
    'fileLayout',
    'busy',
    'onStop',
    'disabled',
    'placeholder',
    'footnote',
    'footer',
    'attachments',
    'minRows',
  ])
  const styles = aiComposer()
  let textarea!: HTMLTextAreaElement

  const resizeTextarea = (): void => {
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 144)}px`
    textarea.style.overflowY = textarea.scrollHeight > 144 ? 'auto' : 'hidden'
  }

  createEffect(() => {
    local.value
    local.minRows
    resizeTextarea()
  })

  const submit = (): void => {
    if (local.disabled || local.busy) return
    const value = local.value.trim()
    const files = local.files ?? []
    if (!value && files.length === 0) return
    local.onSubmit(value, files)
  }

  return (
    <form
      class={styles.form()}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <div class={styles.frame()}>
        <Show when={local.onFilesChange}>
          <div class={styles.dropzone()}>
            <FileDropzone
              compact
              accept={local.accept}
              multiple
              maxSizeBytes={local.maxSizeBytes}
              files={local.files ?? []}
              disabled={local.disabled}
              onFilesChange={(files) => local.onFilesChange?.(files)}
              onReject={local.onFileReject}
              label={local.fileLabel ?? 'Додайте контекст до розмови'}
              fileLayout={local.fileLayout}
              hint={
                local.fileHint ??
                'Перетягніть або виберіть зображення, PDF, документ, креслення чи 3D-файл.'
              }
            />
          </div>
        </Show>

        {local.attachments}
        <div class={styles.field()}>
          <textarea
            ref={textarea}
            data-assistant-autofocus
            name="assistant-message"
            autocomplete="off"
            class={styles.textarea()}
            rows={local.minRows ?? 1}
            value={local.value}
            disabled={local.disabled}
            placeholder={local.placeholder ?? 'Напишіть помічнику…'}
            aria-label="Повідомлення помічнику"
            onInput={(event) => {
              local.onValueChange(event.currentTarget.value)
              resizeTextarea()
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
              event.preventDefault()
              event.currentTarget.form?.requestSubmit()
            }}
          />
          <button
            type={local.busy && local.onStop ? 'button' : 'submit'}
            class={cn(
              styles.action(),
              local.busy || local.value.trim() || local.files?.length
                ? styles.submit()
                : 'bg-surface-sunken text-text-subtle',
            )}
            disabled={
              local.disabled ||
              (local.busy ? !local.onStop : !local.value.trim() && !local.files?.length)
            }
            aria-label={
              local.busy && local.onStop
                ? 'Зупинити відповідь'
                : local.busy
                  ? 'Помічник готує відповідь'
                  : 'Надіслати'
            }
            onClick={() => {
              if (local.busy) local.onStop?.()
            }}
          >
            <Show
              when={!local.busy}
              fallback={
                <Show
                  when={local.onStop}
                  fallback={
                    <span
                      class="size-3 animate-[pulse-soft_1.4s_var(--ease-in-out)_infinite] rounded-pill bg-current motion-reduce:animate-none"
                      aria-hidden="true"
                    />
                  }
                >
                  <IconPlayerStop size={16} strokeWidth={1.5} aria-hidden="true" />
                </Show>
              }
            >
              <IconSend2 size={16} strokeWidth={1.5} aria-hidden="true" />
            </Show>
          </button>
        </div>
        <Show when={local.footer}>
          {(footer) => <div class="flex min-w-0 items-center gap-1 px-2 pb-2">{footer()}</div>}
        </Show>
      </div>

      <Show when={local.footnote}>
        {(footnote) => <p class={styles.footnote()}>{footnote()}</p>}
      </Show>
    </form>
  )
}
